export interface Job<T = unknown, R = unknown> {
  id: string
  queueId: string
  type: string
  data: T
  priority: number
  attempts: number
  maxAttempts: number
  scheduledAt: number
  createdAt: number
  updatedAt: number
  status: JobStatus
  error?: string
  result: R | undefined
  timeout?: number
}

export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface JobOptions {
  priority?: number
  maxAttempts?: number
  delay?: number
  throttle?: number
  timeout?: number
  queueId?: string
}

export type JobHandler<
  T = unknown,
  S extends StateMachineState = StateMachineState,
  C extends StateMachineContext = StateMachineContext,
  R = unknown
> = (
  data: T,
  context: {
    state: S
    job: Job
    context: C
  }
) => Promise<R> | R

export interface JobStats {
  pending: number
  processing: number
  completed: number
  failed: number
  cancelled: number
  total: number
}

export interface QueueStats {
  [queueId: string]: JobStats
}

export interface JobState {
  /**
   * This should be a compound string of
   * queueId and jobType
   */
  id: `${string}-${string}`
  queueId: string
  type: string
  /**
   * Datetime in ms of the last successful run
   */
  lastSuccess: number
}

export interface JobEvent {
  type:
    | 'job-created'
    | 'job-started'
    | 'job-completed'
    | 'job-failed'
    | 'job-cancelled'
  job: Job
}

export interface StateMachineState {
  processing: boolean
}

export interface StateMachineContext {}

export interface JobQueueJobs {}

export type StateActorActions<State extends StateMachineState> =
  { setState: (state: Partial<State>) => void }
